import { isTrackReference, isTrackReferencePinned } from '@livekit/components-core';
import {
  AudioTrack,
  FocusToggle,
  ParticipantContextIfNeeded,
  type ParticipantTileProps,
  TrackRefContextIfNeeded,
  useEnsureTrackRef,
  useFeatureContext,
  useMaybeLayoutContext,
  useParticipantTile,
  useRoomContext,
  VideoTrack,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import React, { useState, type Dispatch, type SetStateAction } from 'react';
import { AdminCustomTrackToggle } from './AdminCustomTrackToggle';
import { CustomTrackToggle } from './CustomTrackToggle';
import ParticipantPlaceholderView from './ParticipantPlaceholderView';
import { Scaling } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';

export const ParticipantView: (
  props: ParticipantTileProps &
    React.RefAttributes<HTMLDivElement> & {
      isAdmin?: boolean;
      userId?: string;
      activeAsset?: boolean;
      workspaceId?: number;
      webinarId?: number;
      sessionId?: number;
      isModerator?: boolean;
      cameraEnabled?: boolean;
      microphoneEnabled?: boolean;
      isSpeaker?: boolean;
      visualIdentity?: WorkspaceVisualIdentity | null;
      onLocalCameraEnabledChange?: Dispatch<SetStateAction<boolean>>;
      onLocalMicrophoneEnabledChange?: Dispatch<SetStateAction<boolean>>;
      publishLocalMediaToRoom?: boolean;
      /** Micro/caméra pris en charge ailleurs (colonne caméras) : cette tuile n'en superpose aucun. */
      externalTrackControls?: boolean;
    },
) => React.ReactNode = /* @__PURE__ */ React.forwardRef<
  HTMLDivElement,
  ParticipantTileProps & {
    isAdmin?: boolean;
    userId?: string;
    activeAsset?: boolean;
    workspaceId?: number;
    webinarId?: number;
    sessionId?: number;
    isModerator?: boolean;
    cameraEnabled?: boolean;
    microphoneEnabled?: boolean;
    isSpeaker?: boolean;
    visualIdentity?: WorkspaceVisualIdentity | null;
    onLocalCameraEnabledChange?: Dispatch<SetStateAction<boolean>>;
    onLocalMicrophoneEnabledChange?: Dispatch<SetStateAction<boolean>>;
    publishLocalMediaToRoom?: boolean;
    externalTrackControls?: boolean;
  }
>(function ParticipantView(
  {
    trackRef,
    children,
    onParticipantClick,
    disableSpeakingIndicator,
    isAdmin,
    userId,
    activeAsset,
    workspaceId,
    webinarId,
    sessionId,
    isModerator,
    cameraEnabled,
    microphoneEnabled,
    isSpeaker,
    visualIdentity: visualIdentityProp,
    onLocalCameraEnabledChange,
    onLocalMicrophoneEnabledChange,
    publishLocalMediaToRoom = true,
    externalTrackControls,
    ...htmlProps
  }: ParticipantTileProps & {
    isAdmin?: boolean;
    userId?: string;
    activeAsset?: boolean;
    workspaceId?: number;
    webinarId?: number;
    sessionId?: number;
    isModerator?: boolean;
    cameraEnabled?: boolean;
    microphoneEnabled?: boolean;
    isSpeaker?: boolean;
    visualIdentity?: WorkspaceVisualIdentity | null;
    onLocalCameraEnabledChange?: Dispatch<SetStateAction<boolean>>;
    onLocalMicrophoneEnabledChange?: Dispatch<SetStateAction<boolean>>;
    publishLocalMediaToRoom?: boolean;
    externalTrackControls?: boolean;
  },
  ref,
) {
  const trackReference = useEnsureTrackRef(trackRef);
  const room = useRoomContext();
  const { currentWorkspace } = useAppSelector((state: RootState) => state.workspace);
  const visualIdentity = visualIdentityProp ?? currentWorkspace?.visualIdentity ?? null;

  // Vérifier si c'est le participant local
  const isLocalParticipant = trackReference.participant.identity === room.localParticipant.identity;
  /** Invité intervenant sur sa propre tuile uniquement (`isSpeaker` décrit le rôle du viewer dans la room). */
  const invitedLocalSpeaker = Boolean(isSpeaker && isLocalParticipant);
  const [isReducedSize, setIsReducedSize] = useState(false);
  const { elementProps } = useParticipantTile<HTMLDivElement>({
    htmlProps,
    disableSpeakingIndicator,
    onParticipantClick,
    trackRef: trackReference,
  });

  const layoutContext = useMaybeLayoutContext();

  const autoManageSubscription = useFeatureContext()?.autoSubscription;

  let widthClass = 'w-full';
  if (activeAsset) {
    widthClass = isReducedSize ? 'w-[175px] !h-[120px]' : 'w-[300px] !h-[175px]';
  }

  const handleSubscribe = React.useCallback(
    (subscribed: boolean) => {
      if (
        trackReference.source &&
        !subscribed &&
        layoutContext &&
        layoutContext.pin.dispatch &&
        isTrackReferencePinned(trackReference, layoutContext.pin.state)
      ) {
        layoutContext.pin.dispatch({ msg: 'clear_pin' });
      }
    },
    [trackReference, layoutContext],
  );

  return (
    <div className="relative h-full w-full">
      <div
        ref={ref}
        style={{ position: 'relative' }}
        {...elementProps}
        className="relative overflow-visible h-full w-full"
      >
        <TrackRefContextIfNeeded trackRef={trackReference}>
          <ParticipantContextIfNeeded participant={trackReference.participant}>
            {children ?? (
              <>
                {(() => {
                  const hasCameraSource =
                    trackReference.source === Track.Source.Camera ||
                    trackReference.source === Track.Source.ScreenShare;
                  const hasVideoTrack =
                    trackReference.publication?.kind === 'video' || hasCameraSource;
                  const isCameraMuted =
                    hasCameraSource &&
                    trackReference.publication?.track &&
                    trackReference.publication.track.isMuted;
                  const showPlaceholder =
                    hasCameraSource && (!trackReference.publication?.track || isCameraMuted);
                  if (showPlaceholder) {
                    return (
                      <div className={`relative overflow-visible ${widthClass} h-full`}>
                        <ParticipantPlaceholderView
                          participant={trackReference.participant}
                          widthClass={widthClass}
                          activeAsset={activeAsset ?? false}
                        />
                        {(isAdmin || invitedLocalSpeaker) && activeAsset && (
                          <div className="absolute left-1/2 -bottom-4 z-50 -translate-x-1/2">
                            <div className="flex items-center gap-2">
                              <button
                                className={cn(
                                  'p-2 rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                                  visualIdentity?.theme === 'DARK' ? 'bg-white' : 'bg-dark-lighter',
                                )}
                                onClick={() => setIsReducedSize(!isReducedSize)}
                              >
                                <Scaling
                                  style={{
                                    color: visualIdentity?.theme === 'DARK' ? '#000' : '#FFFFFF',
                                  }}
                                  className="size-5"
                                />
                              </button>
                              {!isModerator && (
                                <>
                                  <AdminCustomTrackToggle
                                    source={Track.Source.Microphone}
                                    isAdmin={isAdmin}
                                    isSpeaker={isSpeaker}
                                    participantIdentity={trackReference.participant.identity}
                                    roomName={room.name}
                                    workspaceId={workspaceId}
                                    webinarId={webinarId}
                                    sessionId={sessionId}
                                    cameraEnabled={cameraEnabled}
                                    microphoneEnabled={microphoneEnabled}
                                    publishLocalMediaToRoom={publishLocalMediaToRoom}
                                    onLocalCameraEnabledChange={onLocalCameraEnabledChange}
                                    onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
                                  />
                                  <AdminCustomTrackToggle
                                    source={Track.Source.Camera}
                                    isAdmin={isAdmin}
                                    isSpeaker={isSpeaker}
                                    participantIdentity={trackReference.participant.identity}
                                    roomName={room.name}
                                    workspaceId={workspaceId}
                                    webinarId={webinarId}
                                    sessionId={sessionId}
                                    cameraEnabled={cameraEnabled}
                                    microphoneEnabled={microphoneEnabled}
                                    publishLocalMediaToRoom={publishLocalMediaToRoom}
                                    onLocalCameraEnabledChange={onLocalCameraEnabledChange}
                                    onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        )}
                        {!isAdmin && activeAsset && !invitedLocalSpeaker && (
                          <div className="absolute left-1/2 -translate-x-1/2 -bottom-4 z-10">
                            <div className="flex items-center gap-2">
                              <button
                                className={cn(
                                  'p-2 rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                                  visualIdentity?.theme === 'DARK' ? 'bg-white' : 'bg-dark-lighter',
                                )}
                                onClick={() => setIsReducedSize(!isReducedSize)}
                              >
                                <Scaling
                                  style={{
                                    color: visualIdentity?.theme === 'DARK' ? '#000' : '#FFFFFF',
                                  }}
                                  className="size-5"
                                />
                              </button>
                              {isLocalParticipant && (
                                <>
                                  <CustomTrackToggle
                                    source={Track.Source.Microphone}
                                    isAdmin={isAdmin}
                                    cameraEnabled={cameraEnabled}
                                    microphoneEnabled={microphoneEnabled}
                                    publishLocalMediaToRoom={publishLocalMediaToRoom}
                                    onLocalCameraEnabledChange={onLocalCameraEnabledChange}
                                    onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
                                  />
                                  <CustomTrackToggle
                                    source={Track.Source.Camera}
                                    isAdmin={isAdmin}
                                    cameraEnabled={cameraEnabled}
                                    microphoneEnabled={microphoneEnabled}
                                    publishLocalMediaToRoom={publishLocalMediaToRoom}
                                    onLocalCameraEnabledChange={onLocalCameraEnabledChange}
                                    onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }

                  return isTrackReference(trackReference) && hasVideoTrack ? (
                    <div className={`relative overflow-visible ${widthClass} h-full`}>
                      {(isAdmin || invitedLocalSpeaker) && activeAsset && (
                        <div className="absolute left-1/2 -bottom-4 z-50 -translate-x-1/2">
                          <div className="flex items-center gap-2">
                            <button
                              className={cn(
                                'p-2 rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                                visualIdentity?.theme === 'DARK' ? 'bg-white' : 'bg-dark-lighter',
                              )}
                              onClick={() => setIsReducedSize(!isReducedSize)}
                            >
                              <Scaling
                                style={{
                                  color: visualIdentity?.theme === 'DARK' ? '#000' : '#FFFFFF',
                                }}
                                className="size-5"
                              />
                            </button>

                            {!isModerator && (
                              <>
                                <AdminCustomTrackToggle
                                  source={Track.Source.Microphone}
                                  isAdmin={isAdmin}
                                  isSpeaker={isSpeaker}
                                  participantIdentity={trackReference.participant.identity}
                                  roomName={room.name}
                                  workspaceId={workspaceId}
                                  webinarId={webinarId}
                                  sessionId={sessionId}
                                  cameraEnabled={cameraEnabled}
                                  microphoneEnabled={microphoneEnabled}
                                  publishLocalMediaToRoom={publishLocalMediaToRoom}
                                  onLocalCameraEnabledChange={onLocalCameraEnabledChange}
                                  onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
                                />
                                <AdminCustomTrackToggle
                                  source={Track.Source.Camera}
                                  isAdmin={isAdmin}
                                  isSpeaker={isSpeaker}
                                  participantIdentity={trackReference.participant.identity}
                                  roomName={room.name}
                                  workspaceId={workspaceId}
                                  webinarId={webinarId}
                                  sessionId={sessionId}
                                  cameraEnabled={cameraEnabled}
                                  microphoneEnabled={microphoneEnabled}
                                  publishLocalMediaToRoom={publishLocalMediaToRoom}
                                  onLocalCameraEnabledChange={onLocalCameraEnabledChange}
                                  onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
                                />
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {!isAdmin && activeAsset && !invitedLocalSpeaker && (
                        <div className="absolute left-1/2 -bottom-4 z-10 -translate-x-1/2">
                          <div className="flex items-center gap-2">
                            {' '}
                            <button
                              className={cn(
                                'p-2 rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                                visualIdentity?.theme === 'DARK' ? 'bg-white' : 'bg-dark-lighter',
                              )}
                              onClick={() => setIsReducedSize(!isReducedSize)}
                            >
                              <Scaling
                                style={{
                                  color: visualIdentity?.theme === 'DARK' ? '#000' : '#FFFFFF',
                                }}
                                className="size-5"
                              />
                            </button>
                            {isLocalParticipant && (
                              <>
                                <CustomTrackToggle
                                  source={Track.Source.Microphone}
                                  isAdmin={isAdmin}
                                  cameraEnabled={cameraEnabled}
                                  microphoneEnabled={microphoneEnabled}
                                  publishLocalMediaToRoom={publishLocalMediaToRoom}
                                  onLocalCameraEnabledChange={onLocalCameraEnabledChange}
                                  onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
                                />
                                <CustomTrackToggle
                                  source={Track.Source.Camera}
                                  isAdmin={isAdmin}
                                  cameraEnabled={cameraEnabled}
                                  microphoneEnabled={microphoneEnabled}
                                  publishLocalMediaToRoom={publishLocalMediaToRoom}
                                  onLocalCameraEnabledChange={onLocalCameraEnabledChange}
                                  onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
                                />
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      <VideoTrack
                        trackRef={trackReference}
                        onSubscriptionStatusChanged={handleSubscribe}
                        manageSubscription={autoManageSubscription}
                        className={cn(
                          'w-full h-full',
                          trackReference.source === Track.Source.ScreenShare
                            ? 'object-contain'
                            : 'rounded-lg object-cover',
                        )}
                        style={
                          trackReference.source === Track.Source.ScreenShare
                            ? { backgroundColor: visualIdentity?.backgroundColor || '#000000' }
                            : undefined
                        }
                        controls={false}
                        playsInline
                        controlsList="nodownload nofullscreen noremoteplayback"
                        disablePictureInPicture
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </div>
                  ) : (
                    isTrackReference(trackReference) && (
                      <AudioTrack
                        trackRef={trackReference}
                        onSubscriptionStatusChanged={handleSubscribe}
                      />
                    )
                  );
                })()}
              </>
            )}

            <FocusToggle trackRef={trackReference} />
          </ParticipantContextIfNeeded>
        </TrackRefContextIfNeeded>
      </div>
      {(isAdmin || invitedLocalSpeaker) &&
      !activeAsset &&
      !isModerator &&
      !externalTrackControls ? (
        <div className="z-50 absolute top-2 right-2">
          <div className="flex items-center gap-2">
            <AdminCustomTrackToggle
              source={Track.Source.Microphone}
              isAdmin={isAdmin}
              isSpeaker={isSpeaker}
              participantIdentity={trackReference.participant.identity}
              roomName={room.name}
              workspaceId={workspaceId}
              webinarId={webinarId}
              sessionId={sessionId}
              cameraEnabled={cameraEnabled}
              microphoneEnabled={microphoneEnabled}
              publishLocalMediaToRoom={publishLocalMediaToRoom}
              onLocalCameraEnabledChange={onLocalCameraEnabledChange}
              onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
            />
            <AdminCustomTrackToggle
              source={Track.Source.Camera}
              isAdmin={isAdmin}
              isSpeaker={isSpeaker}
              participantIdentity={trackReference.participant.identity}
              roomName={room.name}
              workspaceId={workspaceId}
              webinarId={webinarId}
              sessionId={sessionId}
              cameraEnabled={cameraEnabled}
              microphoneEnabled={microphoneEnabled}
              publishLocalMediaToRoom={publishLocalMediaToRoom}
              onLocalCameraEnabledChange={onLocalCameraEnabledChange}
              onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
            />
          </div>
        </div>
      ) : null}
      {!isAdmin && isLocalParticipant && !invitedLocalSpeaker && !activeAsset && !isModerator ? (
        <div className="z-10 absolute top-2 right-2">
          <div className="flex items-center gap-2">
            <CustomTrackToggle
              source={Track.Source.Microphone}
              isAdmin={isAdmin}
              cameraEnabled={cameraEnabled}
              microphoneEnabled={microphoneEnabled}
              publishLocalMediaToRoom={publishLocalMediaToRoom}
              onLocalCameraEnabledChange={onLocalCameraEnabledChange}
              onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
            />
            <CustomTrackToggle
              source={Track.Source.Camera}
              isAdmin={isAdmin}
              cameraEnabled={cameraEnabled}
              microphoneEnabled={microphoneEnabled}
              publishLocalMediaToRoom={publishLocalMediaToRoom}
              onLocalCameraEnabledChange={onLocalCameraEnabledChange}
              onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
});
